import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DataQuery from 'data/DataQuery';
import { Billings_DownloadDocument } from 'data/endpoints';
import IFileService from 'data/IFileService';
import RoundButton from 'features/shared/components/buttons/RoundButton';
import DateFormatter from 'features/shared/components/formatters/DateFormatter';
import PriceFormatter from 'features/shared/components/formatters/PriceFormatter';
import { useInjection } from 'infrastructure/ioc/ioc.react';
import { IocTypes } from 'infrastructure/ioc/ioc.types';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import InvoiceLineCalculationDTO from '../dtos/InvoiceLineCalculationDTO';
import ContractLink from './ContractLink';
import RoundDropdownButton from 'features/shared/components/buttons/RoundDropdownButton';
import { Dropdown } from 'react-bootstrap';
import DropdownActionItem from 'features/shared/components/dropdown/DropdownActionItem';
import { useTranslation } from 'react-i18next';
import InvoiceLineDownloadDetailDTO from '../dtos/InvoiceLineDownloadDetailDTO';
import { LocationDescriptor } from 'history';
import { getRouteContainsFuelingParam } from 'features/shared/helpers/routeHelper';
import { containsFuelingCommodity } from 'features/contracts/helpers/commodityDetailHelper';

interface Props {
  invoiceLine: InvoiceLineCalculationDTO;
  downloadDetails?: InvoiceLineDownloadDetailDTO[];
}

const InvoiceLineCalculation = ({ invoiceLine, downloadDetails }: Props) => {
  // translations
  const tLabels = useTranslation("invoice\\invoice").t;
  const tDownloadDetailType = useTranslation("invoice\\enums\\invoiceDownloadDetailType").t;

  // DI
  const fileService = useInjection<IFileService>(IocTypes.FileService);

  // router
  const location = useLocation();
  const history = useHistory();

  // handlers
  const downloadDocumentHandler = (downloadDetail: InvoiceLineDownloadDetailDTO) => {
    let query: DataQuery = {
      url: Billings_DownloadDocument,
      params: new URLSearchParams([
        ["InvoiceId", invoiceLine.invoiceId!],
      ]),
      routeParams: new URLSearchParams([
        ["billingId", downloadDetail.invoiceLineId!],
        ["downloadType", downloadDetail.downloadDetailType!]
      ])
    };
    
    fileService.downloadFile(query);
  }
  
  // helpers
  const getBillingDetailRoute = (): LocationDescriptor => { 
    let route = `billing/${invoiceLine.invoiceLineId}`;
    return getRouteContainsFuelingParam(route, [invoiceLine.commodityDetail!]);
  }

  // sub-renders
  const renderButtons = () => {
    if (downloadDetails && downloadDetails.length > 0) {
      return (
        <Dropdown>
          <RoundDropdownButton menuId={`invoice-line-grid-menu-${invoiceLine.invoiceLineId}`} />
          {renderActionsMenu()}
        </Dropdown>
      );
    } else {
      return (
        <RoundButton
          asLink
          to={getBillingDetailRoute()}
        />
      );
    }
  };

  const renderActionsMenu = () => {
    return (
      <Dropdown.Menu>
        <DropdownActionItem 
          title={tLabels("invoiceLineCalculation.billingDetail")} 
          icon="search" 
          onClick={() =>  history.push(getBillingDetailRoute())}
        />
        {renderDownloadActions()}
      </Dropdown.Menu>
    );
  }

  const renderDownloadActions = () => {
    return downloadDetails?.map((item, index) => (
      <DropdownActionItem
        title={tDownloadDetailType(item.downloadDetailType!)}
        icon="download"
        onClick={() => downloadDocumentHandler(item)}
        key={index}
      />
    ));
  };

  // render
  return (
    <div className="row no-gutters align-items-center">
      <div className="col">
        <div className="row no-gutters align-items-center">
          <div className="col-12 col-sm-5 col-lg-6">
            <div className="d-flex flex-sm-column flex-lg-row align-items-start align-items-lg-center">
              <span className="mr-2 text-nowrap">{tLabels("invoiceLineCalculation.billingFor")} </span>
              <ContractLink 
                entityLink={invoiceLine.entityLink!} 
                isFuelingContract={containsFuelingCommodity([invoiceLine.commodityDetail!])}
              />
            </div>
          </div>
          <div className="col-12 col-sm-7 col-lg-6 pl-sm-3">
            <div className="row no-gutters align-items-center">
              <div className="col-auto col-sm-6 order-1 order-sm-0 text-right text-sm-left">
                <div className="d-flex flex-wrap flex-sm-column align-items-center align-items-sm-stretch text-muted">
                  <span className="mr-2 d-sm-none">
                    <FontAwesomeIcon icon="calendar-alt"/>
                  </span>
                  <span className="small">
                    <DateFormatter date={invoiceLine.periodFrom!} />
                  </span>
                  <span className="d-sm-none mx-1 small">-</span>
                  <span className="small">
                    <DateFormatter date={invoiceLine.periodTo!} />
                  </span>
                </div>
              </div>
              <div className="col-auto col-sm-6 text-right pr-2 pr-sm-0">
                <PriceFormatter amount={invoiceLine.amount!} currency={invoiceLine.currency!} />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="col-auto pl-3">
        {renderButtons()}
      </div>
    </div>
  )
}

export default InvoiceLineCalculation;
